import MapboxDraw, { constants, DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";
import { LngLat } from "mapbox-gl";
import { Utils } from "mapbox-gl-draw-snap-mode";

import {
  captureBeforeDeleteFeatures,
  captureBeforeMoveFeatures,
  initializeValidation,
  validateAfterDelete,
  validateAfterMove,
  ValidationState,
} from "fond/draw/validation/helper";
import { safeNearestPointOnLine } from "fond/turf";
import { clickEventBBox } from "fond/utils/mapbox";

import { snapWithPointPriority } from "./snap/helper";
import {
  ColocatedFeature,
  filterEditableFeatures,
  findColocatedFeaturesAtLocation,
  getFeatureTarget,
  isFeatureInEditableScope,
  moveColocatedFeatures,
  removeGuides,
} from "./helper";

const { createSnapList } = Utils;

const {
  modes,
  lib: {
    CommonSelectors: { isFeature, isActiveFeature, isShiftDown },
    moveFeatures,
  },
} = MapboxDraw;

interface DrawCustomModeState extends ValidationState {
  dragMoving: boolean;
  initialDragPanState: boolean;
  dragMoveLocation: LngLat;
  snapList: mapboxgl.MapboxGeoJSONFeature[] | null;
  vertices: Array<[number, number]> | null;
  delta: LngLat;
  colocatedFeatures: ColocatedFeature[] | null;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
  getUniqueIds(this: DrawCustomModeThis, allFeatures: mapboxgl.MapboxGeoJSONFeature[]): string[];
}

const simpleSelect: DrawCustomModeExtension<DrawCustomModeState> = {
  ...modes.simple_select,

  onSetup: function (opts) {
    const state = modes.simple_select.onSetup?.call(this, opts);
    state.options = this._ctx.options;
    state.map = this.map;
    state.delta = { lng: 0, lat: 0 };
    state.colocatedFeatures = null;

    // Initialize validation
    initializeValidation.call(this, state);

    removeGuides.call(this);

    return state;
  },

  onClick: function (state, event) {
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);
    modes.simple_select.onClick?.call(this, state, event);
  },

  onMouseUp: function (state, event) {
    // We clear out the snapList, vertices, and colocatedFeatures after mouse up
    state.snapList = null;
    state.vertices = null;
    state.colocatedFeatures = null;

    modes.simple_select.onMouseUp?.call(this, state, event);

    // Get currently selected features (the ones that were moved)
    const movedFeatures = this.getSelected().map((f) => f.toGeoJSON() as Feature);

    // Validate features after move (synchronous to ensure immediate visual update)
    validateAfterMove.call(this, state, movedFeatures);
  },

  /**
   * We customise the onMouseDown event to allow the user to drag an unselected
   * Feature without first selecting it & to provide snapping while in this mode.
   */
  onMouseDown: function (state, event) {
    state.initialDragPanState = this.map.dragPan.isEnabled();

    // Set featureTarget early so vertex detection works
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);

    // Check if clicking on a vertex - change to direct_select immediately for drag-on-click
    if (event.featureTarget?.properties?.meta === constants.meta.VERTEX) {
      // Prevent map panning while dragging vertex
      this.map.dragPan.disable();

      // Change to direct_select immediately with startPos for immediate dragging
      this.changeMode(constants.modes.DIRECT_SELECT, {
        featureId: event.featureTarget.properties.parent,
        coordPath: event.featureTarget.properties.coord_path,
        startPos: event.lngLat,
      });
      return;
    }

    if (isFeature(event)) {
      if (!event.featureTarget) {
        return;
      }

      const featureId = event.featureTarget.properties?.id;
      const feature = this.getFeature(featureId);

      if (feature) {
        // Check if the clicked feature can be moved
        const clickedFeature = feature.toGeoJSON() as Feature;
        const editableFeatures = filterEditableFeatures(this._ctx.options, [clickedFeature]);

        // If the clicked feature is not editable, don't allow dragging
        if (editableFeatures.length === 0) {
          return;
        }

        // onMouseDown occurs prior to the feature being selected, so we need to handle the selected features directly.
        const selectedFeatures = isActiveFeature(event) || isShiftDown(event) ? this.getSelected().map((f) => f.toGeoJSON() as Feature) : [];

        // Capture features for validation before move
        const featuresToMove = [...selectedFeatures, feature.toGeoJSON() as Feature];
        captureBeforeMoveFeatures.call(this, state, featuresToMove);

        // Find colocated features if topologicalEditing is enabled
        // This must be done BEFORE creating the snap list so we can exclude them
        if (this._ctx.options.topologicalEditing) {
          const colocated: ColocatedFeature[] = [];

          // Get all features that will be moved (selected + clicked feature)
          const allFeaturesToMove = [...selectedFeatures];
          // If the clicked feature is not already selected, include it too
          if (!allFeaturesToMove.find((f) => f.id === feature.id)) {
            allFeaturesToMove.push(feature.toGeoJSON() as Feature);
          }

          // Get IDs of all features being moved (to exclude from colocation)
          const movingFeatureIds = allFeaturesToMove.map((f) => f.id || f.properties?.id);

          // Collect vertices from ALL features being dragged
          allFeaturesToMove.forEach((f) => {
            const drawFeature = this.getFeature(f.id || f.properties?.id);
            if (!drawFeature) return;

            const verticesToCheck: Array<[number, number]> = [];

            if (drawFeature.type === "Point") {
              const coords = drawFeature.getCoordinates() as [number, number];
              verticesToCheck.push(coords);
            } else if (drawFeature.type === "LineString") {
              const coords = drawFeature.getCoordinates() as Array<[number, number]>;
              verticesToCheck.push(...coords);
            } else if (drawFeature.type === "Polygon") {
              const coords = drawFeature.getCoordinates() as Array<Array<[number, number]>>;
              coords.forEach((ring) => verticesToCheck.push(...ring));
            }

            // For each vertex of this feature, find colocated features
            verticesToCheck.forEach((vertexCoords) => {
              // Create a bounding box around the vertex using standard buffer
              const point = this.map.project(vertexCoords);
              const bbox = clickEventBBox({ point } as mapboxgl.MapMouseEvent);

              // Query features at this location
              const nearbyFeatures = this.featuresAt(null, bbox, "click");

              // Filter out ALL features being moved from nearby features
              const nearbyExcludingMoving = nearbyFeatures.filter((nf) => !movingFeatureIds.includes(nf.properties?.id));

              // Find colocated features at this location, filtered by editingLayerIds
              const allowedLayerIds = this._ctx.options.editOptions?.editingLayerIds;
              const colocatedAtVertex = findColocatedFeaturesAtLocation(vertexCoords, nearbyExcludingMoving, drawFeature.id, allowedLayerIds, (id) =>
                this.getFeature(id)
              );
              colocated.push(...colocatedAtVertex);
            });
          });

          state.colocatedFeatures = colocated.length > 0 ? colocated : null;
        }

        // Calculate the features available for snapping (if not already calculated)
        // Colocated features are filtered out during snapping in snapWithPointPriority
        if (state.snapList == null && state.vertices == null) {
          const [snapList, vertices] = createSnapList(this.map, this._ctx.api, feature, this._ctx.options.snapOptions?.snapGetFeatures);
          state.snapList = snapList;
          state.vertices = vertices;
        }

        state.delta = new LngLat(0, 0);

        if (feature.type === "LineString") {
          const nearestPoint = safeNearestPointOnLine(feature, [event.lngLat.lng, event.lngLat.lat]);
          if (nearestPoint) {
            state.delta = new LngLat(
              event.lngLat.lng - nearestPoint.geometry.coordinates[0],
              event.lngLat.lat - nearestPoint.geometry.coordinates[1]
            );
          }
        } else if (feature.type === "Point") {
          state.delta = new LngLat(event.lngLat.lng - feature.getCoordinates()[0], event.lngLat.lat - feature.getCoordinates()[1]);
        }

        // If the feature<Point> is not currently selected we manually click on
        // the feature to select it, allowing for the user to click and drag in one action
        if (feature.type === "Point" && !isActiveFeature(event) && !isShiftDown(event)) {
          this.clickOnFeature?.(state, event);
          this.startOnActiveFeature?.(state, event);
        }
      }
    }
    modes.simple_select.onMouseDown?.call(this, state, event);
  },

  /**
   * We customize this to only select features from editingLayerIds.
   * When editing a sublayer, also filters by the sublayer's filter expression.
   * All features from editable layers can be selected simultaneously.
   */
  getUniqueIds: function (allFeatures) {
    const editingLayerIds = this._ctx.options.editOptions?.editingLayerIds;
    const editableSublayers = this._ctx.options.editOptions?.editableSublayers || [];

    // If editingLayerIds is not defined, use the base implementation
    if (editingLayerIds === undefined) {
      // @ts-expect-error - getUniqueIds exists at runtime but not in types
      return modes.simple_select.getUniqueIds?.call(this, allFeatures) || [];
    }

    // If no layers are editable, no features can be selected
    if (editingLayerIds.length === 0) {
      return [];
    }

    if (!allFeatures.length) return [];

    // Filter features to include ANY editable layer (not just a single editingLayerId)
    const featuresToProcess = allFeatures.filter((f) => {
      // For features with user_layerId, we need to get the actual DrawFeature
      // to evaluate sublayer filters properly
      const featureId = f.properties?.id;
      if (!featureId) return false;

      const drawFeature = this.getFeature(featureId);
      if (!drawFeature) return false;

      const actualFeature = drawFeature.toGeoJSON() as Feature;

      // Use shared helper to check if feature is in editable scope
      return isFeatureInEditableScope(actualFeature, editingLayerIds, editableSublayers);
    });

    // Extract unique IDs
    const uniqueIds = new Set<string>();
    featuresToProcess.forEach((feature) => {
      const id = feature.properties?.id;
      if (id !== undefined) {
        uniqueIds.add(id);
      }
    });

    return Array.from(uniqueIds);
  },

  /**
   * We customise the dragMove function to allow for points to snap.
   */
  dragMove: function (state, event) {
    state.dragMoving = true;
    event.originalEvent.stopPropagation();

    const { lng, lat } = snapWithPointPriority(state, event);
    // The event lngLat is mutated to include the delta, which is the distance
    // from the actual feature the user clicked. This allows us to snap to the
    // correct location. Without this the snapping would be off by the offset
    // of the initial click
    event.lngLat = new LngLat(lng + state.delta.lng, lat + state.delta.lat);
    const delta = {
      lng: event.lngLat.lng - state.dragMoveLocation.lng,
      lat: event.lngLat.lat - state.dragMoveLocation.lat,
    };

    // Filter selected features to only move editable ones
    const selectedFeatures = this.getSelected();
    const editableFeatures = filterEditableFeatures(
      this._ctx.options,
      selectedFeatures.map((f) => f.toGeoJSON() as Feature)
    );
    const editableFeatureIds = new Set(editableFeatures.map((f) => f.id || f.properties?.id));
    const featuresToMove = selectedFeatures.filter((f) => editableFeatureIds.has(f.id));

    moveFeatures(featuresToMove, delta);

    // Move colocated features if topologicalEditing is enabled
    if (state.colocatedFeatures && state.colocatedFeatures.length > 0) {
      moveColocatedFeatures(state.colocatedFeatures, delta, (id) => this.getFeature(id));
    }

    state.dragMoveLocation = event.lngLat;
  },

  onTrash: function (state) {
    // Get selected features before they're deleted
    const selectedFeatures = this.getSelected().map((f) => f.toGeoJSON() as Feature);

    // Filter features based on multi-layer editing configuration
    const featuresToDelete = filterEditableFeatures(this._ctx.options, selectedFeatures);

    // If no features can be deleted, return early
    if (featuresToDelete.length === 0) {
      return;
    }

    // Capture colocated features before deletion
    captureBeforeDeleteFeatures.call(this, state, featuresToDelete);

    // Delete features using deleteFeature which automatically fires the delete event
    const featureIds = featuresToDelete.map((f) => f.id || f.properties?.id).filter((id): id is string | number => id !== undefined);

    if (featureIds.length > 0) {
      // deleteFeature accepts both string and array, but TS types are incomplete
      this.deleteFeature(featureIds as unknown as string);
    }

    // Validate affected features after deletion (synchronous to ensure immediate visual update)
    validateAfterDelete.call(this, state);
  },
};

export default simpleSelect;
